import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { FormField } from '@/firebase/formService';
import { evaluateFormula, type FormulaContext } from '@/utils/formulaEvaluator';

interface FormulaTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  formula: string;
  fields: FormField[];
  fieldIndex: number; // Index of the calculated field being tested
}

const FormulaTestModal: React.FC<FormulaTestModalProps> = ({ 
  isOpen, 
  onClose,
  formula,
  fields,
  fieldIndex
}) => {
  const [testValues, setTestValues] = useState<Record<string, any>>({});
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize test values with sample data
  useEffect(() => {
    if (!isOpen || !fields) return;
    
    const initialValues: Record<string, any> = {};
    const availableFields = fields.filter(f => f.label && f.type !== 'calculated' && f.order < (fields[fieldIndex]?.order || 0));
    
    availableFields.forEach(field => {
      switch(field.type) {
        case 'number':
          initialValues[field.label] = 10;
          break;
        case 'reference':
          // For table forms, use first item's reference value if available
          const firstItemValue = field.itemReferenceValues ? Object.values(field.itemReferenceValues)[0] : undefined;
          initialValues[field.label] = firstItemValue !== undefined ? firstItemValue : 50;
          break;
        case 'text':
          initialValues[field.label] = 'Sample';
          break;
        default:
          initialValues[field.label] = '';
      }
    });
    
    setTestValues(initialValues);
    setResult(null);
    setError(null);
  }, [isOpen, fields, fieldIndex]);

  const testFormula = () => {
    if (!formula || !fields) {
      setError('No formula provided');
      return;
    }

    try {
      const context: FormulaContext = {
        fieldValues: { ...testValues },
        fields: fields
      };
      
      const evaluated = evaluateFormula(formula, context);
      setResult(evaluated);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Formula evaluation failed');
      setResult(null);
    }
  };

  if (!isOpen) return null;

  const availableFields = fields.filter(f => f.label && f.type !== 'calculated' && f.order < (fields[fieldIndex]?.order || 0));

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Test Formula</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700">
            Enter test values to see what your formula returns:
          </p>

          {/* Formula Display */}
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <p className="text-xs text-gray-600 mb-1">Formula:</p>
            <code className="text-sm font-mono text-gray-900">{formula || '(empty)'}</code>
          </div>

          {/* Test value inputs */}
          {availableFields.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-800">Test Values:</p>
              {availableFields.map(field => (
                <div key={field.label}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.type === 'reference' && field.itemReferenceValues && Object.keys(field.itemReferenceValues).length > 0 && (
                      <span className="ml-2 text-gray-500 italic">(Reference: {Object.values(field.itemReferenceValues)[0]})</span>
                    )}
                  </label>
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={testValues[field.label] !== undefined ? String(testValues[field.label]) : ''}
                    onChange={(e) => {
                      let value: any = e.target.value;
                      if (field.type === 'number') {
                        value = e.target.value === '' ? '' : Number(e.target.value);
                      } else if (field.type === 'reference') {
                        // Reference fields can be text or number - try to parse as number, otherwise keep as text
                        const trimmed = e.target.value.trim();
                        value = trimmed === '' ? '' : (isNaN(Number(trimmed)) ? trimmed : Number(trimmed));
                      }
                      setTestValues({
                        ...testValues,
                        [field.label]: value
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        testFormula();
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={field.type === 'reference' ? String(field.itemReferenceValues ? Object.values(field.itemReferenceValues)[0] || '' : '') : 'Enter value...'}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                No fields available to test. Add fields before this calculated field to test the formula.
              </p>
            </div>
          )}

          {/* Test Button */}
          <div>
            <button
              type="button"
              onClick={testFormula}
              disabled={!formula || availableFields.length === 0}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              🧪 Test Formula
            </button>
          </div>

          {/* Result */}
          {result !== null && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-xs font-medium text-green-900 mb-2">Result:</p>
              <p className="text-lg font-mono text-green-800 font-semibold">{String(result)}</p>
              <p className="text-xs text-green-700 mt-2">
                Type: {typeof result === 'number' ? 'Number' : typeof result === 'boolean' ? 'Boolean' : 'Text'}
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-xs font-medium text-red-900 mb-1">Error:</p>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormulaTestModal;

